import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as MoveBackward } from '../../../assets/icons/device-management/move-backward.svg';
import { ReactComponent as MoveForward } from '../../../assets/icons/device-management/move-forward.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAllAccountsId } from '../../../redux/slices/AccountManagementSlice';
import SelectField from '../../../shared-components/select-field/SelectField';

/**
 * PartnerSearch component to search and select partner accounts.
 * @returns {any} The rendered component
 */
function PartnerSearch() {
  const { t, i18n } = useTranslation(['account', 'userRole']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');

  const { accountIds = [] } = useAppSelector((state) => state.accounts);

  // Initial fetch of partner account IDs
  useEffect(() => {
    if (accountIds.length === 0) {
      dispatch(fetchAllAccountsId());
    }
  }, [dispatch, accountIds.length]);

  // Convert accounts list to dropdown options
  const dropdownData = useMemo(() => {
    const formatted = accountIds.map((partner: any) => ({
      label: partner.accountName || partner.name,
      value: partner.accountCustomId,
    }));

    return formatted.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
  }, [accountIds]);

  /**
   * Set selected partner in search parameters
   * @param {string} value - The selected partner accountCustomId
   */
  const handleSelectPartner = (value: string | { label: string; value: string }) => {
    const selectedValue = typeof value === 'string' ? value : value.value;
    if (accountCustomId === selectedValue) return;

    if (id) {
      const pathParts = location.pathname.split('/');
      pathParts[pathParts.length - 1] = selectedValue;
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('stationId');
      parameters.delete('deviceId');
      const searchString = parameters.toString();
      const newPath = pathParts.join('/') + (searchString ? `?${searchString}` : '');
      navigate(newPath, { replace: true });
    } else {
      const parameters = new URLSearchParams(searchParameters);
      parameters.set('accountCustomId', selectedValue);
      // Clear sub-panel param when switching partner to keep context clean
      parameters.delete('stationId');
      parameters.delete('deviceId');
      setSearchParameters(parameters, { replace: true });
    }
  };

  /**
   * Get next or previous partner for navigation
   * @param {any} direction - The direction to navigate
   */
  const handleNextOrPrevious = (direction: 'next' | 'prev') => {
    if (!accountCustomId || dropdownData.length === 0) return;

    const currentIndex = dropdownData.findIndex((partner) => partner.value === accountCustomId);

    if (currentIndex === -1) return;

    let targetIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex >= dropdownData.length) targetIndex = dropdownData.length - 1;

    const nextOption = dropdownData[targetIndex];

    if (nextOption) {
      handleSelectPartner(nextOption.value);
    }
  };

  return (
    <div className="partner-search-wraps">
      <div className="partner-search-container" dir={i18n.dir()}>
        <span
          role="presentation"
          onClick={() => handleNextOrPrevious('prev')}
          className={i18n.dir() === 'rtl' ? 'rotate-180' : ''}
          title={t('previous')}>
          <MoveBackward />
        </span>

        <SelectField
          name="partnerAccount"
          placeholder={t('selectAccount')}
          selectedValue={accountCustomId || ''}
          onChangeValue={(_, value) => handleSelectPartner(value)}
          options={dropdownData}
          enableSearch
        />

        <span
          role="presentation"
          onClick={() => handleNextOrPrevious('next')}
          className={i18n.dir() === 'rtl' ? 'rotate-180' : ''}
          title={t('next')}>
          <MoveForward />
        </span>
      </div>
    </div>
  );
}

export default PartnerSearch;
